import React, { useState } from 'react';
import { Mail, UserPlus, Users, X } from 'lucide-react';
import { useStore } from 'context/StoreContext';
import { Card, CardHeader } from 'components/common/Card';
import { Button } from 'components/common/Button';
import { Badge } from 'components/common/Badge';
import { Dialog } from 'components/common/Dialog';
import { formatDate } from 'utils/formatters';
import { useApi } from 'hooks/useApi';
import { MemberService } from 'services/org/member.service';
import { Pagination } from 'components/common/Pagination';

const PAGE_SIZE = 8;

const roleLabels = { owner: 'Owner', manager: 'Manager', associate: 'Associate' };

export default function UsersPage() {
  const { org, stores } = useStore();
  const [membersPage, setMembersPage] = useState(1);
  const [invitationsPage, setInvitationsPage] = useState(1);
  const { data: members, refetch: refreshMembers } = useApi(() => MemberService.listMembers(org?.id ?? '', membersPage, PAGE_SIZE), [org?.id, membersPage]);
  const { data: invitations, refetch: refreshInvitations } = useApi(() => MemberService.listInvitations(org?.id ?? '', invitationsPage, PAGE_SIZE), [org?.id, invitationsPage]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'manager' | 'associate'>('manager');
  const [assignedStoreIds, setAssignedStoreIds] = useState<string[]>([]);
  const [notice, setNotice] = useState('');
  const [inviteError, setInviteError] = useState('');

  const toggleStore = (storeId: string) => {
    setAssignedStoreIds(ids => ids.includes(storeId) ? ids.filter(id => id !== storeId) : [...ids, storeId]);
  };

  const sendInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return;
    setInviteError('');
    try {
      await MemberService.invite(org?.id ?? '', normalizedEmail, role, assignedStoreIds);
      refreshInvitations();
      setInviteOpen(false);
      setEmail('');
      setAssignedStoreIds([]);
      setNotice(`Invitation sent to ${normalizedEmail}.`);
      window.setTimeout(() => setNotice(''), 3500);
    } catch (error: any) {
      setInviteError(error.message || 'Unable to send invitation.');
    }
  };

  const revokeInvite = async (id: string) => { await MemberService.revokeInvitation(org?.id ?? '', id); refreshInvitations(); };
  const removeMember = async (id: string) => { await MemberService.remove(org?.id ?? '', id); refreshMembers(); };
  const changeRole = async (id: string, nextRole: 'manager' | 'associate') => { await MemberService.updateRole(org?.id ?? '', id, nextRole); refreshMembers(); };

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users size={20} className="text-gray-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Users & access</h1>
            <p className="text-sm text-gray-500">Invite employees and control store access.</p>
          </div>
        </div>
        <Button onClick={() => setInviteOpen(true)}><UserPlus size={15} /> Invite user</Button>
      </div>

      {notice && <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{notice}</div>}

      <Card padding={false}>
        <div className="px-5 pt-5"><CardHeader title="Organization members" subtitle={`${members?.meta.total ?? 0} active users`} /></div>
        <div className="divide-y divide-gray-100">
          {(members?.data ?? []).map(member => (
            <div key={member.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
                {(member.full_name || member.email).split(' ').map(part => part[0]).join('').slice(0, 2)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">{member.full_name || member.email}</p>
                <p className="text-xs text-gray-500">{member.email} · Joined {formatDate(member.created_at)}</p>
              </div>
              <Badge variant={member.role === 'owner' ? 'info' : 'default'}>{roleLabels[member.role]}</Badge>
              <div className="flex items-center gap-2">
                {member.role !== 'owner' && (
                  <select value={member.role} onChange={event => changeRole(member.id, event.target.value as 'manager' | 'associate')} className="rounded border border-gray-300 px-2 py-1.5 text-xs">
                    <option value="manager">Manager</option>
                    <option value="associate">Associate</option>
                  </select>
                )}
                {member.role !== 'owner' && <Button size="sm" variant="ghost" onClick={() => removeMember(member.id)}>Remove</Button>}
              </div>
            </div>
          ))}
        </div>
        {members && <Pagination page={membersPage} pageSize={PAGE_SIZE} total={members.meta.total} onPageChange={setMembersPage} />}
      </Card>

      <Card padding={false}>
        <div className="px-5 pt-5"><CardHeader title="Pending invitations" subtitle="Invitations expire after 7 days." /></div>
        <div className="divide-y divide-gray-100">
          {(invitations?.data.length ?? 0) === 0 ? <p className="px-5 pb-5 text-sm text-gray-500">No pending invitations.</p> : (invitations?.data ?? []).map(invite => (
            <div key={invite.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <Mail size={18} className="text-gray-400" />
              <div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-900">{invite.email}</p><p className="text-xs text-gray-500">Sent {formatDate(invite.created_at)} · Expires {formatDate(invite.expires_at)}</p></div>
              <Badge>{roleLabels[invite.role]}</Badge>
              <Button size="sm" variant="ghost" onClick={() => revokeInvite(invite.id)}><X size={13} /> Revoke</Button>
            </div>
          ))}
        </div>
        {invitations && <Pagination page={invitationsPage} pageSize={PAGE_SIZE} total={invitations.meta.total} onPageChange={setInvitationsPage} />}
      </Card>

      <Dialog open={inviteOpen} title="Invite a team member" onClose={() => { setInviteError(''); setInviteOpen(false); }}>
        <form onSubmit={sendInvite} className="space-y-4">
          {inviteError && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{inviteError}</div>}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Work email</label>
            <input type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="employee@example.com" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
            <select value={role} onChange={event => setRole(event.target.value as 'manager' | 'associate')} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
              <option value="manager">Manager — can act on assigned stores</option>
              <option value="associate">Associate — read-only on assigned stores</option>
            </select>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Store access</p>
            <div className="space-y-2">
              {stores.map(store => <label key={store.id} className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={assignedStoreIds.includes(store.id)} onChange={() => toggleStore(store.id)} className="accent-blue-600" />{store.name}</label>)}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="secondary" onClick={() => setInviteOpen(false)}>Cancel</Button><Button type="submit">Send invitation</Button></div>
        </form>
      </Dialog>
    </div>
  );
}
